import { vibrate } from '@orbomb/platform';
import type { SimulationEvent } from '@orbomb/sim';

export interface Tone {
  hz: number;
  /** Pitch falls to this by the end of the tone. A knock, not a held note. */
  endHz?: number;
  seconds: number;
  /** Wait this long before the tone, so two hits stay separate. */
  delay?: number;
  wave: OscillatorType;
  gain: number;
}

const ARM: Tone = { hz: 880, seconds: 0.07, wave: 'square', gain: 0.04 };
const HIT: Tone = { hz: 220, seconds: 0.12, wave: 'triangle', gain: 0.06 };
const BUMP: Tone = { hz: 180, seconds: 0.04, wave: 'sine', gain: 0.03 };
/** Rising two-note chime for a 1Up pickup. */
const ONE_UP: readonly Tone[] = [
  { hz: 988, seconds: 0.09, wave: 'square', gain: 0.035 },
  { hz: 1319, seconds: 0.16, delay: 0.09, wave: 'square', gain: 0.035 },
];
const WIN: Tone = { hz: 523, seconds: 0.28, wave: 'sine', gain: 0.06 };
/** The recording is near full scale. This leaves room for a second blast on top. */
const BLAST_GAIN = 0.7;
/** Sits just over the blast, which is playing at the same moment. */
const STRIKE_GAIN = 0.9;

/** How many copies of the explosion recording this tick should start. */
export function sampleBlasts(events: readonly { type: string }[]): number {
  return events.filter((event) => event.type === 'DETONATED').length;
}

/** How many copies of the totem-strike recording this tick should start. */
export function sampleStrikes(events: readonly { type: string }[]): number {
  return events.filter((event) => event.type === 'TOTEM_HIT').length;
}

/** Oscillator cues for one tick. Detonations and totem hits use recordings, not a tone. */
export function tonesFor(events: readonly { type: string; strength?: number }[]): Tone[] {
  const matchOver = events.some((event) => event.type === 'MATCH_ENDED');
  const tones: Tone[] = [];
  for (const event of events) {
    switch (event.type) {
      case 'ARMED':
        tones.push(ARM);
        break;
      case 'PLAYER_HIT':
        if (!matchOver) tones.push(HIT);
        break;
      case 'IMPACT':
        if (!matchOver && (event.strength ?? 0) > 1.2) tones.push(BUMP);
        break;
      case 'BONUS_COLLECTED':
        if (!matchOver) tones.push(...ONE_UP);
        break;
      case 'MATCH_ENDED':
        tones.push(WIN);
        break;
      default:
        break;
    }
  }
  return tones;
}

/** Tiny Web Audio cues. Visuals remain authoritative. */
export class Sfx {
  volume = 0.8;
  muted = false;
  /** Ducks the match track for the length of a blast. */
  onBlast: (() => void) | null = null;
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private readonly buffers = new Map<string, Promise<AudioBuffer | null>>();
  private readonly live: { osc: OscillatorNode; gain: GainNode }[] = [];
  private readonly clips: { src: AudioBufferSourceNode; gain: GainNode }[] = [];

  constructor(
    private readonly blastUrl: string,
    private readonly strikeUrl: string,
  ) {}

  apply(settings: { volume: number; muted: boolean }): void {
    this.volume = settings.volume;
    this.muted = settings.muted;
    if (this.master) this.master.gain.value = this.outputGain();
  }

  /** Short tone so the volume slider can be judged. */
  preview(): void {
    const ctx = this.ensure();
    const play = () => this.play({ hz: 660, seconds: 0.16, wave: 'sine', gain: 0.07 });
    if (ctx.state === 'running') play();
    else void ctx.resume().then(play);
  }

  handle(events: readonly SimulationEvent[]): void {
    if (events.length === 0) return;
    if (events.some((event) => event.type === 'MATCH_ENDED')) this.stopAll();
    const blasts = sampleBlasts(events);
    if (blasts > 0) this.onBlast?.();
    for (let i = 0; i < blasts; i++) this.blast();
    for (let i = 0; i < sampleStrikes(events); i++) this.strike();
    for (const tone of tonesFor(events)) this.play(tone);
    for (const event of events) {
      if (event.type === 'DETONATED') vibrate(40);
      else if (event.type === 'TOTEM_HIT') vibrate([18, 80, 18, 80, 22]);
      else if (event.type === 'PLAYER_HIT') vibrate([12, 30, 12]);
    }
  }

  resume(): void {
    void this.ensure().resume();
    void this.loadBuffer(this.blastUrl);
    void this.loadBuffer(this.strikeUrl);
  }

  /** Shared context and master gain, so music follows the same volume and mute. */
  bus(): { ctx: AudioContext; out: AudioNode } {
    const ctx = this.ensure();
    return { ctx, out: this.master ?? ctx.destination };
  }

  stopAll(): void {
    const ctx = this.ctx;
    const now = ctx?.currentTime ?? 0;
    for (const { osc, gain } of this.live) this.fade(gain, osc, now);
    for (const { src, gain } of this.clips) this.fade(gain, src, now);
    this.live.length = 0;
    this.clips.length = 0;
  }

  private fade(gain: GainNode, source: { stop: (when: number) => void }, now: number): void {
    try {
      gain.gain.cancelScheduledValues(now);
      gain.gain.setValueAtTime(gain.gain.value, now);
      gain.gain.linearRampToValueAtTime(0.0001, now + 0.03);
      source.stop(now + 0.04);
    } catch {
      /* already stopped */
    }
  }

  private outputGain(): number {
    return this.muted ? 0 : this.volume;
  }

  private ensure(): AudioContext {
    if (!this.ctx) {
      this.ctx = new AudioContext();
      this.master = this.ctx.createGain();
      this.master.gain.value = this.outputGain();
      this.master.connect(this.ctx.destination);
    }
    return this.ctx;
  }

  private loadBuffer(url: string): Promise<AudioBuffer | null> {
    const existing = this.buffers.get(url);
    if (existing) return existing;
    const ctx = this.ensure();
    const pending = fetch(url)
      .then((res) => {
        if (!res.ok) throw new Error(`audio ${res.status}`);
        return res.arrayBuffer();
      })
      .then((bytes) => ctx.decodeAudioData(bytes))
      .catch(() => null);
    this.buffers.set(url, pending);
    return pending;
  }

  private blast(): void {
    this.playClip(this.blastUrl, BLAST_GAIN);
  }

  private strike(): void {
    this.playClip(this.strikeUrl, STRIKE_GAIN);
  }

  private playClip(url: string, level: number): void {
    const ctx = this.ensure();
    const fire = () => {
      void this.loadBuffer(url).then((buffer) => {
        if (!buffer || ctx.state !== 'running') return;
        this.startClip(buffer, level);
      });
    };
    if (ctx.state === 'running') fire();
    else void ctx.resume().then(fire);
  }

  private startClip(buffer: AudioBuffer, level: number): void {
    const ctx = this.ensure();
    const src = ctx.createBufferSource();
    const gain = ctx.createGain();
    src.buffer = buffer;
    gain.gain.value = level;
    src.connect(gain);
    gain.connect(this.master ?? ctx.destination);
    while (this.clips.length >= 6) {
      try {
        this.clips.shift()?.src.stop();
      } catch {
        /* already stopped */
      }
    }
    const clip = { src, gain };
    this.clips.push(clip);
    src.onended = () => {
      const i = this.clips.indexOf(clip);
      if (i >= 0) this.clips.splice(i, 1);
    };
    src.start();
  }

  private play(tone: Tone): void {
    const ctx = this.ensure();
    if (ctx.state !== 'running') {
      void ctx.resume().then(() => {
        if (ctx.state === 'running') this.play(tone);
      });
      return;
    }
    const osc = ctx.createOscillator();
    const g = ctx.createGain();
    osc.type = tone.wave;
    const start = ctx.currentTime + (tone.delay ?? 0);
    osc.frequency.setValueAtTime(tone.hz, start);
    if (tone.endHz !== undefined && tone.endHz < tone.hz) {
      osc.frequency.exponentialRampToValueAtTime(tone.endHz, start + tone.seconds);
    }
    g.gain.setValueAtTime(tone.gain, start);
    g.gain.exponentialRampToValueAtTime(0.0001, start + tone.seconds);
    osc.connect(g);
    g.connect(this.master ?? ctx.destination);
    const voice = { osc, gain: g };
    while (this.live.length >= 16) {
      try {
        this.live.shift()?.osc.stop();
      } catch {
        /* already stopped */
      }
    }
    this.live.push(voice);
    osc.onended = () => {
      const i = this.live.indexOf(voice);
      if (i >= 0) this.live.splice(i, 1);
    };
    osc.start(start);
    osc.stop(start + tone.seconds);
  }
}
